import React from 'react';
import { FormRenderProps } from '@progress/kendo-react-form';
import { bypassLoginRefresh, enums, scrollApp } from '../../../../utils';
import Button from '../../../base/button';
import styled from 'styled-components';
import { FormContext } from '../../../../contexts/form-context';
import { RefreshContext } from '../../../../contexts/refresh-context';
import ConfirmDialogModal from '../../../base/confirm-dialog-modal';
import { findFormStepIndex, onPrevClick, onStartOverClick } from '../handlers';
import { CustomStepProps, processNavigationObject } from '../utils';
import { useAutosaveDelete } from '../../../../hooks/use-data';
import { useLocation } from 'react-router-dom';

export interface IButtonsProps {
  currentStepIndex: number;
  setCurrentStepIndex: React.Dispatch<React.SetStateAction<number>>;
  formRenderProps: FormRenderProps;
  form: any;
  formSteps: any;
  currentStepHasLeavePlanGraph: boolean;
  submitting: boolean;
  displayConfirmationModal: boolean;
  displayRestartWarningModal: boolean;
  setDisplayConfirmationModal: React.Dispatch<React.SetStateAction<boolean>>;
  setDisplayRestartWarningModal: React.Dispatch<React.SetStateAction<boolean>>;
  stepGoBack: number;
  setStepGoBack: React.Dispatch<React.SetStateAction<number>>;
  steps: CustomStepProps[];
  setFormStepIndex: React.Dispatch<React.SetStateAction<number>>;
  scrollToElement: string;
  sectionName?: string;
}

const ButtonContainer = styled.div`
  margin-top: 32px;
`;

const Buttons = ({
  currentStepIndex,
  setCurrentStepIndex,
  formRenderProps,
  form,
  formSteps,
  currentStepHasLeavePlanGraph,
  submitting,
  displayConfirmationModal,
  setDisplayConfirmationModal,
  displayRestartWarningModal,
  setDisplayRestartWarningModal,
  stepGoBack,
  setStepGoBack,
  steps,
  setFormStepIndex,
  scrollToElement,
  sectionName,
}: IButtonsProps) => {
  const { formContext, allowNavBack, allowNavForward } =
    React.useContext(FormContext);
  const { isRefreshing } = React.useContext(RefreshContext);

  const isLastStep = steps.length - 1 === currentStepIndex;
  const location = useLocation();
  const { lastPath } = processNavigationObject({
    location,
    history: undefined as any,
    match: undefined as any,
  });
  const { mutate: mutateAutosaveDelete, isSuccess: autosaveDeleteSuccessful } =
    useAutosaveDelete(lastPath);

  const handleDelete = React.useCallback(() => {
    mutateAutosaveDelete();
  }, [mutateAutosaveDelete]);

  React.useEffect(() => {
    if (autosaveDeleteSuccessful) {
      bypassLoginRefresh();
      window.location.reload();
    }
  }, [autosaveDeleteSuccessful]);

  const handleConfirm = async () => {
    if (stepGoBack > -1) {
      setCurrentStepIndex(() => Math.max(stepGoBack, 0));
      setFormStepIndex(findFormStepIndex(steps[stepGoBack], formSteps));
      setStepGoBack(-1);
    } else {
      // current step index
      const csi = currentStepIndex - 1;
      setCurrentStepIndex(csi);
      setFormStepIndex(findFormStepIndex(steps[csi], formSteps));
    }

    if (scrollToElement.length === 0) {
      scrollApp();
    }
  };

  // Analytics objects for data attribs - start ----------------------------------------
  function isNextStepConfirmation(currentStepIndex: number): boolean {
    const nextStep = steps[currentStepIndex + 1];
    return nextStep?.label === 'Confirmation';
  }

  let errorMsgJson: string | undefined;
  const errors: Record<string, string> | undefined = formRenderProps?.errors;

  const errorPairs: [string, string][] = errors
    ? Object.entries(errors).filter(
        ([, value]) => value != null && value.trim() !== '',
      )
    : [];

  if (errors && Object.keys(errors).length > 0) {
    errorMsgJson =
      errorPairs.length > 0 ? JSON.stringify(errorPairs) : undefined;
  }

  const isFormValid = formRenderProps?.valid;
  const isFormValidText = isFormValid ? 'success' : 'error';

  let nextBtnAttrib2: string = '';
  if (isLastStep) {
    nextBtnAttrib2 = 'before you exit';
  } else if (isNextStepConfirmation(currentStepIndex)) {
    nextBtnAttrib2 = 'claim step - Confirmation';
  } else {
    nextBtnAttrib2 = sectionName ?? '';
  }

  const nextBtnAttrib3 = isLastStep
    ? `submit_claim_${isFormValidText}`
    : `claim_navigation_${isFormValidText}`;

  const nextBtnAttrib4 =
    isLastStep && isFormValid ? formContext.formName : errorMsgJson;
  // Analytics objects for data attribs - end ----------------------------------------

  return (
    (form && formSteps && (
      <ButtonContainer
        id="form-buttons"
        className="tw-flex tw-justify-between tw-mb-5 tw-w-full"
      >
        <div className="tw-flex tw-gap-3">
          {currentStepIndex !== 0 ? (
            <Button
              id="form-buttons-back"
              keyId="previous"
              data-gtm-dl={JSON.stringify({
                attribute1: 'file a claim',
                attribute2: `claim step - ${sectionName}`,
                attribute3: 'claim_navigation',
                attribute4: 'undefined',
              })}
              onClick={(e: React.SyntheticEvent<any>) =>
                onPrevClick({
                  event: e,
                  setCurrentStepIndex,
                  formContext,
                  setDisplayConfirmationModal,
                  currentStepHasLeavePlanGraph,
                  steps,
                  formSteps,
                  currentStepIndex,
                  setFormStepIndex,
                })
              }
              buttonStyle={enums.ButtonTypes.secondary}
              disabled={submitting || !allowNavBack}
            >
              Back
            </Button>
          ) : undefined}
          <Button
            id="form-buttons-reset"
            data-gtm-dl={JSON.stringify({
              attribute1: 'file a claim',
              attribute2: `claim step - ${sectionName}`,
              attribute3: 'start_over_click',
              attribute4: 'undefined',
            })}
            onClick={(e: React.SyntheticEvent<any>) =>
              onStartOverClick({
                event: e,
                setDisplayRestartWarningModal,
              })
            }
          >
            Start Over
          </Button>
        </div>
        <Button
          className="tw-ml-3"
          id="form-buttons-next"
          keyId="next"
          data-gtm-event="cta_click"
          data-gtm-dl={JSON.stringify({
            attribute1: 'file a claim',
            attribute2: nextBtnAttrib2 ?? 'undefined',
            attribute3: nextBtnAttrib3 ?? 'undefined',
            attribute4: nextBtnAttrib4 ?? 'undefined',
          })}
          onClick={formRenderProps.onSubmit}
          disabled={
            submitting || !allowNavForward || (isLastStep && isRefreshing)
          }
        >
          {isLastStep ? 'Submit' : 'Next'}
        </Button>
        {displayConfirmationModal && (
          <ConfirmDialogModal
            title="You are about to reset your estimated leave plan"
            description="By returning to a previous step your estimated leave plan could be reset.  Are you sure you want to continue?"
            confirmButtonText="Yes"
            cancelButtonText="No"
            handleConfirm={handleConfirm}
            modalCallback={() => setDisplayConfirmationModal(false)}
          />
        )}
        {displayRestartWarningModal && (
          <ConfirmDialogModal
            title="Are you sure you want to start over?"
            description="Starting over will erase all previously saved answers."
            confirmButtonText="Yes"
            cancelButtonText="No"
            handleConfirm={handleDelete}
            modalCallback={() => setDisplayRestartWarningModal(false)}
            data-gtm-dl={JSON.stringify({
              attribute1: 'file a claim',
              attribute2: `claim step - ${sectionName}`,
              attribute3: 'claim_navigation',
              attribute4: 'undefined',
            })}
          />
        )}
      </ButtonContainer>
    )) ||
    null
  );
};

export default Buttons;
